import * as React from 'react'

import { Box, Button, type Icon } from '@factorialco/deprecated-design-system'
import type { Form } from 'factorial-form'
import {
  CustomResourcesResourceValuesInput,
  CustomTableAttachValuesDocument,
  EditCustomTablePageResourceValueFragment,
  EditCustomTablePageSchemaFieldValueFragment,
  EmployeesCustomTablesListPageDocument,
  NewCustomTablePageSchemaFragment,
} from 'generated/resources/graphql'
import { keyBy } from 'lodash'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useMutation } from 'modules/core/hooks/useMutation'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { InputItem } from 'modules/custom_resources/components/CustomFields/CustomFieldsForm/InputItem'
import { createForm } from 'modules/employees/components/CustomResources/SchemaComponent/Form/utils/createForm'
import { RouteContext } from 'modules/employees/pages/CustomTablesListPage/context'

import LoadingFooter from './LoadingFooter'
import LoadingState from './LoadingState'

interface Props {
  illustration?: React.ComponentProps<typeof Icon.Large>['icon']
  title?: string
  description?: string
  employeeId: number
  schema: NewCustomTablePageSchemaFragment
  resourceValue?: EditCustomTablePageResourceValueFragment
  currency: string
  loading: boolean
  canRemoveCustomTableRecords: boolean
  onClose: () => void
}

const FormModal = ({
  illustration = 'table-file',
  title = i18n.t('custom_tables.modal.title'),
  description = i18n.t('custom_tables.modal.description'),
  employeeId,
  schema,
  resourceValue,
  currency,
  loading,
  canRemoveCustomTableRecords,
  onClose,
}: Props) => {
  const rootRoute = React.useContext(RouteContext)
  const customValues: EditCustomTablePageSchemaFieldValueFragment[] =
    resourceValue?.customValues ?? []
  const customValuesByFieldId = keyBy(customValues, 'fieldId')
  const resourceId = schema?.resourcesConnection?.nodes[0]?.id

  const forms = React.useRef(
    schema.fields.reduce<Form[]>((acc, field) => {
      const customField = field.field
      if (customField) {
        acc.push(
          createForm({
            schema,
            field: customField,
            value: customValuesByFieldId[customField.id],
          })
        )
      }
      return acc
    }, [])
  ).current

  const [updateValue] = useMutation(CustomTableAttachValuesDocument, {
    extractorFn: (data) => data.customResourcesMutations.attachValuesCustomresourcesResource,
    refetchQueries: [EmployeesCustomTablesListPageDocument],
  })

  const getStringValue = (fieldType: string, value?: string | number | null): string => {
    if (!value) return ''
    const stringValue = value.toString()

    if (fieldType === 'date') {
      const uiDate = stringToDate(stringValue, 'ui')
      return i18nDateToString(uiDate, 'backend')
    }

    return stringValue
  }

  const { performAction } = useAsyncAction(async (closePortal: ClosePortalFn) => {
    const values: CustomResourcesResourceValuesInput[] = forms
      .filter((form) => form.get('editable').value && form.isDirty)
      .map((form) => {
        const valueId = form.get('value_id').value ? Number(form.get('value_id').value) : null
        const fieldType = form.get('field_type').value
        const formValue = form.get('value').value
        return {
          fieldId: Number(form.get('field_id').value),
          valueId: valueId,
          value: getStringValue(fieldType, formValue),
        }
      })

    const result = await updateValue({
      variables: {
        employeeId,
        resourceId: resourceId,
        schemaId: schema.id,
        valueId: resourceValue?.id,
        values,
      },
    })

    const handled = handleResult(result, (errors) => {
      if (errors) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_error'),
        })
      }
    })

    if (handled.success) {
      closePortal()
    }
  })

  const submitEnabled = forms.some((f) => f.isDirty)

  const footer = loading ? (
    <LoadingFooter />
  ) : (
    <Box flexDirection="row" justifyContent="flexEnd" gap="s8" background="grey100">
      {!!resourceValue && canRemoveCustomTableRecords && rootRoute && schema && (
        <Button.Router
          to={rootRoute.customTablesDelete(String(schema.id), String(resourceValue.id))}
          type="negative"
        >
          {i18n.t('custom_tables.modal.delete')}
        </Button.Router>
      )}

      <Button disabled={!submitEnabled} submit>
        {i18n.t('custom_tables.modal.submit')}
      </Button>
    </Box>
  )

  return (
    <Modal
      illustration={illustration}
      title={title}
      description={description}
      onSubmit={performAction}
      onClose={onClose}
      footer={footer}
    >
      {loading ? (
        <LoadingState />
      ) : (
        forms.map((form: Form, key: number) => (
          <FormRow key={key}>
            <InputItem form={form} currency={currency} />
          </FormRow>
        ))
      )}
    </Modal>
  )
}

export default observer(FormModal)
